import { useNavigate } from "react-router-dom";
import { Project, apiClient } from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import { Clock, MoreVertical, FileText, Film, File, Presentation, ArrowRight, Trash2, Users, Crown } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { showSuccess, showError } from "@/utils/toast";
import { useIsMobile } from "@/hooks/use-mobile";

interface DashboardProjectCardProps {
  project: Project;
  score?: number;
}

export const DashboardProjectCard = ({ project, score }: DashboardProjectCardProps) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const isMobile = useIsMobile();

  // Check if current user is owner or collaborator
  const isOwner = project.user_id === user?._id;
  const isCollaborator = !isOwner && project.collaborators?.some(c => c.user_id === user?._id);

  // Handle project card click
  const handleProjectClick = (e: React.MouseEvent) => {
    // Only prevent navigation if clicking directly on a button or dropdown trigger
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button') || target.closest('[role="button"]')) {
      return;
    }

    // If project is reviewed (manual approval complete), go to main project route
    // (which will handle first-time vs returning user flow)
    // Otherwise, go to initial assessment page
    let targetPath;
    if (project.status === 'draft') {
      targetPath = `/projects/create/step-2?projectId=${project._id}`;
    } else if (project.is_reviewed) {
      targetPath = `/project-v2/${project._id}`;
    } else {
      targetPath = `/initial-assessment?projectId=${project._id}`;
    }

    navigate(targetPath);
  };

  // Fetch scripts for the project to show snippet (only if not already included in project data)
  const { data: fetchedScripts } = useQuery({
    queryKey: ["projectScripts", project._id],
    queryFn: () => apiClient.getScriptsByProject(project._id),
    staleTime: 1000 * 60 * 5,
    enabled: !(project as any).scripts, // Only fetch if scripts not already included
  });

  // Use scripts from project data if available, otherwise use fetched scripts
  const scripts = (project as any).scripts || fetchedScripts;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => apiClient.deleteProject(project._id),
    onSuccess: () => {
      showSuccess("Project deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setShowDeleteDialog(false);
    },
    onError: (error: Error) => {
      console.error("Delete project error:", error);
      showError(error.message || "Failed to delete project. Please check your permissions.");
    },
  });

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  const getStatusBadge = (status: Project["status"]) => {
    switch (status) {
      case "analysis_complete":
        return {
          bg: "bg-green-50",
          text: "text-green-700",
          border: "border-green-100",
          label: "Score Ready",
          indicatorColor: "bg-green-500",
        };
      case "context_complete":
      case "script_uploaded":
        return {
          bg: "bg-blue-50",
          text: "text-blue-700",
          border: "border-blue-100",
          label: "Processing",
          indicatorColor: "bg-blue-500",
          animate: true,
        };
      case "pending":
        return {
          bg: "bg-primary/10",
          text: "text-primary",
          border: "border-primary/20",
          label: "Analyzing",
          indicatorColor: "bg-primary",
          animate: true,
        };
      case "deal_closed":
        return {
          bg: "bg-emerald-50",
          text: "text-emerald-700",
          border: "border-emerald-100",
          label: "Deal Closed",
          indicatorColor: "bg-emerald-500",
        };
      default:
        return {
          bg: "bg-secondary",
          text: "text-muted-foreground",
          border: "border-border",
          label: "Draft",
          indicatorColor: "bg-muted-foreground",
        };
    }
  };

  const getProjectIcon = (status: Project["status"]) => {
    if (status === 'deal_closed') return <Presentation className="w-4 h-4" />;
    if (status === 'analysis_complete') return <FileText className="w-4 h-4" />;
    if (status === 'pending') return <Film className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  }

  const getIconContainerStyle = (status: Project["status"]) => {
    if (status === 'deal_closed') return "bg-emerald-50 border-emerald-100 text-emerald-600";
    if (status === 'analysis_complete') return "bg-secondary border-border text-primary";
    if (status === 'pending') return "bg-secondary border-border text-muted-foreground";
    return "bg-secondary border-border text-muted-foreground";
  }

  const statusConfig = getStatusBadge(project.status);

  const getProjectStatus = (score: number) => {
    if (score >= 80) return { label: "On track", color: "text-green-500" };
    if (score >= 60) return { label: "Needs work", color: "text-yellow-500" };
    return { label: "At risk", color: "text-red-500" };
  };

  const projectStatus = score !== undefined ? getProjectStatus(score) : null;

  return (
    <>
      <div onClick={handleProjectClick} className="folder-wrapper group relative flex flex-col mt-3 cursor-pointer">
        <div className="folder-shadow relative flex flex-col w-full">
          {/* Folder Tab */}
          <div className="relative z-20 h-[22px] w-[35%] min-w-[100px] max-w-[140px] bg-card border-t border-l border-r border-border rounded-t-lg -mb-[1px] ml-[1px] group-hover:border-neutral-300 group-hover:bg-secondary transition-colors flex items-center px-3">
            <div className={`w-1.5 h-1.5 rounded-full mr-2 ${statusConfig.indicatorColor} ${statusConfig.animate ? 'animate-pulse' : ''}`}></div>
            <span className="text-[9px] font-bold text-muted-foreground tracking-wider uppercase">
              Project
            </span>
            {isCollaborator && (
              <div className="ml-auto" title="You are a collaborator">
                <Users className="w-3 h-3 text-blue-500" />
              </div>
            )}
            {isOwner && project.collaborators && project.collaborators.length > 0 && (
              <div className="ml-auto" title={`${project.collaborators.length} collaborator${project.collaborators.length > 1 ? 's' : ''}`}>
                <Crown className="w-3 h-3 text-amber-500" />
              </div>
            )}
          </div>

          {/* Folder Body */}
          <div className="relative z-10 bg-card border border-border rounded-b-xl rounded-tr-xl p-5 group-hover:border-neutral-300 transition-colors flex flex-col h-full min-h-[180px]">
            {/* Header */}
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded border flex items-center justify-center ${getIconContainerStyle(project.status)}`}>
                  {getProjectIcon(project.status)}
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-foreground text-sm font-cabinet tracking-tight group-hover:text-primary transition-colors line-clamp-1">
                    {isMobile && project.title.length > 30
                      ? `${project.title.slice(0, 30)}...`
                      : project.title}
                  </h3>
                  <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-ibm">
                    <Clock className="w-3 h-3" />
                    <span>{formatDistanceToNow(new Date(project.updated_at), { addSuffix: true, includeSeconds: true })}</span>
                  </div>
                </div>
              </div>
              {isOwner && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <button className="text-muted-foreground hover:text-foreground transition-colors">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive cursor-pointer"
                      onClick={handleDeleteClick}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Project
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Description or Script Snippet */}
            <div className="flex-1 mb-4 pl-1">
              {scripts && scripts.length > 0 && scripts[0].extracted_text ? (
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                    Script Preview
                  </p>
                  <div className="text-[10px] text-muted-foreground/70 font-mono leading-relaxed bg-secondary/30 p-2 rounded border border-border/30">
                    {scripts[0].extracted_text.split('\n').slice(0, 3).map((line, i) => (
                      <p key={i} className="line-clamp-1">{line.trim() || '...'}</p>
                    ))}
                  </div>
                </div>
              ) : project.description ? (
                <p className="text-xs text-muted-foreground font-ibm line-clamp-2 leading-relaxed">
                  {project.description}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground font-ibm line-clamp-2 leading-relaxed">
                  No description provided.
                </p>
              )}
            </div>

            <div className="mt-auto border-t border-border pt-3">
              <div className="flex flex-wrap gap-y-2 items-center justify-between">
                {/* Status Pill or View Recommendations Button */}
                {project.status === 'analysis_complete' ? (
                  (project.is_reviewed) ? (
                    <div className="flex items-center gap-1.5 bg-primary/10 text-primary px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide transition-all">
                      <span>Recommendations are ready</span>
                      <ArrowRight className="w-3 h-3" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 bg-yellow-50 text-yellow-700 border border-yellow-200 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide transition-all">
                      <Clock className="w-3 h-3" />
                      <span>Pending Review</span>
                    </div>
                  )
                ) : (
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-medium ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border uppercase tracking-wide`}>
                    {statusConfig.animate && (
                      <span className="relative flex h-1.5 w-1.5">
                        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${statusConfig.text} opacity-75`}></span>
                        <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${statusConfig.indicatorColor}`}></span>
                      </span>
                    )}
                    {statusConfig.label}
                  </span>
                )}

                {/* Project Status - Hide for pending review projects */}
                {!(project.status === 'analysis_complete' && !project.is_reviewed) && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground font-ibm">Status</span>
                    {project.status === 'analysis_complete' && projectStatus ? (
                      <span className={`text-sm font-bold font-cabinet uppercase tracking-wide ${projectStatus.color}`}>
                        {projectStatus.label}
                      </span>
                    ) : (
                      <span className="text-sm font-bold font-cabinet uppercase tracking-wide text-muted-foreground">
                        Pending
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{project.title}"? This action cannot be undone and will permanently delete all associated data including scripts and scores.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};